import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    // JC-EVO 09/30/2026 a11y (WCAG 2.4.3, audit T7): every open/close of a <details> takes a token BEFORE anything is scheduled; work
    // that runs after a newer open/close (rapid toggling) does nothing, so a closed drawer never gets menu-open or a focus trap back
    // and a reopened one is never reset.
    const jcOp = (details.jcA11yOp = (details.jcA11yOp || 0) + 1);
    requestAnimationFrame(() => {
      if (jcOp !== details.jcA11yOp) return;
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(
        drawer || details,
        () => {
          if (jcOp !== details.jcA11yOp || !details.isConnected || !details.hasAttribute('open')) return;
          trapFocus(details);
          // JC-EVO 09/30/2026 a11y (T7): Horizon only armed the trap and left focus on the Menu button under the drawer: move it to
          // the opened panel's first usable control (Close, or Back in a submenu) unless it is already inside.
          const panel = drawer || details;
          if (panel.contains(document.activeElement)) return;
          const first = Array.from(panel.querySelectorAll('button, [href], input, select, textarea, summary, [tabindex]')).find(
            (el) =>
              el.tabIndex >= 0 &&
              !el.hasAttribute('disabled') &&
              !el.closest('[inert], [aria-hidden="true"]') &&
              el.getClientRects().length > 0 &&
              getComputedStyle(el).visibility !== 'hidden'
          );
          if (first instanceof HTMLElement) first.focus({ preventScroll: true });
        },
        { subtree: false }
      );
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
    const jcOp = (details.jcA11yOp = (details.jcA11yOp || 0) + 1); // JC-EVO 09/30/2026 a11y (T7): see open()
    const jcMainOp = this.refs.details.jcA11yOp; // a submenu callback may re-arm the main trap only if the main drawer did not change since

    onAnimationEnd(
      drawer || details,
      () => {
        if (jcOp !== details.jcA11yOp) return; // a newer open/close owns this <details> now
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else if (this.refs.details.jcA11yOp === jcMainOp && this.refs.details.hasAttribute('open')) {
          trapFocus(this.refs.details); // JC-EVO a11y (T7): only while the same opening of the main drawer is still current
        }
        // JC-EVO 09/30/2026 a11y (WCAG 2.4.3, audit T7): Escape or Close dropped focus to <body>. Put it back on this panel's
        // summary (the Menu button, or the submenu's item) when it was inside the closed panel or lost.
        const active = document.activeElement;
        if (!active || active === document.body || (details.contains(active) && active !== summary)) {
          summary.focus({ preventScroll: true });
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
